import React, { useContext } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import Header from './Header';
import MachineManager from './MachineManager/MachineManager';
import PlaylistManager from './PlaylistManager/PlaylistManager';
import PlaytreeManager from './PlaytreeManager/PlaytreeManager';
import Login from './Login';
import AuthProvider, { AuthContext } from './AuthContext'; // Import both AuthProvider and AuthContext
import DataProvider from './DataContexts/DataContext';
import PlaylistDataProvider from './DataContexts/PlaylistDataContext';
import PlaytreeDataProvider from './DataContexts/PlaytreeDataContext';
import UploadProvider from './DataContexts/UploadContext'; // Import UploadProvider
import MachineGroupProvider from './DataContexts/MachineGroupContext';
import './App.css';
import PlaylistHeader from './PlaylistManager/PlaylistHeader';
import LibraryHeader from './MusicManager/LibraryHeader';
import MachineProvider from './DataContexts/MachineContext';
import AdsProvider from './DataContexts/AdsContext';
import BackgroundProvider from './DataContexts/BackgroundContext';
import ImageLibraryProvider from './DataContexts/ImageLibraryContext';
import MachineHeader from './MachineManager/MachineHeader';
import MachineGroups from './MachineManager/MachineGroups';
import DashboardPage from './Dashboard/DashboardPage';
import ConfigurationPage from './Configuration/ConfigurationPage';
import InboxPage, { InboxIndexRedirect } from './Inbox/InboxPage';
import InboxCountsProvider from './Inbox/InboxCountsContext';
import SideNav from './navigation/SideNav';
import ThemeProvider from './ThemeContext';

const App = () => {
  return (
    // DJB-118: the theme wraps everything (login included) since it applies
    // as a root attribute the CSS tokens key off
    <ThemeProvider>
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
};

const AppRoutes = () => {
  const { authData, authLoading } = useContext(AuthContext);

  if (authLoading) {
    return <p>Loading...</p>;
  }

  if (!authData) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <DataProvider>
      <PlaylistDataProvider>
        <PlaytreeDataProvider>
          <UploadProvider>
            <BackgroundProvider>
            <ImageLibraryProvider>
            <MachineProvider>
              <AdsProvider>
                <MachineGroupProvider>
                <AdsProvider>
                  {/* DJB-114: on desktop the side nav runs full height on the
                      left, and the header lives inside the main column so it
                      only spans the content area. On mobile the nav hides
                      itself (DJB-106) and the header pill menu takes over. */}
                  {/* DJB-264: the inbox to-do counts feed the nav badges and
                      the inbox pages, so they live above both */}
                  <InboxCountsProvider>
                  <div className="app-shell">
                    <SideNav />
                    <div className="app-main">
                    <Header />
                    <div className="content">
                    <Routes>
                      {/* DJB-123: Accounts is the default page on login and
                          reload, even though the nav lists Dashboard first */}
                      <Route path="/" element={<Navigate to="/machines" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/musicmanager" element={<LibraryHeader/>} />
                      {/* Old standalone Upload page (DJB-96): lives on as the
                          Upload tab inside the Library */}
                      <Route path="/trackupload" element={<Navigate to="/musicmanager?tab=upload" replace />} />
                      <Route path="/playlists" element={<PlaylistHeader />} />
                      <Route path="/playlistmanager" element={<PlaylistManager />} />
                      <Route path="/playtreemanager" element={<PlaytreeManager />} />
                      <Route path="/machines" element={<MachineHeader />} />
                      <Route path="/machine-manager" element={<MachineManager />} />
                      <Route path="/machine-groups" element={<MachineGroups />} />
                      {/* Old Visuals page (DJB-84): Assign lives on in Curator >
                          Subscriptions & Restrictions, Designer in Media Studio */}
                      <Route path="/ads-manager" element={<Navigate to="/playlists?tab=blocklists" replace />} />
                      {/* DJB-109: Configuration is the last page in the nav */}
                      <Route path="/configuration" element={<ConfigurationPage />} />
                      {/* DJB-264: one route per request kind; /inbox itself
                          (and the old ?tab= links) redirect to it */}
                      <Route path="/inbox" element={<InboxIndexRedirect />} />
                      <Route path="/inbox/contact" element={<InboxPage key="contact" kind="contact" />} />
                      <Route path="/inbox/songs" element={<InboxPage key="songs" kind="songs" />} />
                      <Route path="/login" element={<Navigate to="/" />} />
                    </Routes>
                    </div>
                    </div>
                  </div>
                  </InboxCountsProvider>
                </AdsProvider>
                </MachineGroupProvider>
              </AdsProvider>
            </MachineProvider>
            </ImageLibraryProvider>
            </BackgroundProvider>
          </UploadProvider>
        </PlaytreeDataProvider>
      </PlaylistDataProvider>
    </DataProvider>
  );
};

export default App;
